<template>
   <b-container id="adminMain">
      <b-card class="mb-3">
         <b-card-body>
            <b-form class="mb-3" inline>
                  <b-input v-model="uid" type="text" :state="uidValid" placeholder="User id" />
                  <b-form-invalid-feedback :state="uidValid">
                     Your user ID must 28 be characters long.
                  </b-form-invalid-feedback>
                  <b-input v-model="displayName" type="text" :state="displayNameValid" placeholder="Naam" class="ml-2 mr-2" inline/>
                  <b-form-invalid-feedback :state="displayNameValid">
                     Your user ID must at least be 1 characters long.
                  </b-form-invalid-feedback>
                  <b-button class="m-1" @click="switchUser" >Switch</b-button>
            </b-form>
            <b-form>
               <b-form-group autofocus label="Add admin">
                  <b-form-input type="email" v-model="email" placeholder="Enter email"/>
                  <b-button @click="addAdminRole" class="m-2">Submit</b-button>
               </b-form-group>
               <b-form-group label="Recipes">
                  <b-button class="m-2" @click="cleanRecipes(user)" >Clean ingredients</b-button>
               </b-form-group>
            </b-form>
         </b-card-body>
      </b-card>

      <!-- Tag Management -->
      <b-card>
         <b-card-header>
            <h5 class="mb-0">Tag beheer</h5>
         </b-card-header>
         <b-card-body>
            <!-- Nieuwe tag toevoegen -->
            <b-form @submit.prevent="addTag" class="mb-4">
               <b-row>
                  <b-col cols="5">
                     <b-form-input 
                        v-model="newTag.id" 
                        placeholder="Tag ID (bijv. italian)" 
                        :state="newTagIdValid"
                        size="sm" />
                     <small class="text-muted">Unieke identifier, lowercase, geen spaties</small>
                  </b-col>
                  <b-col cols="5">
                     <b-form-input 
                        v-model="newTag.name" 
                        placeholder="Weergavenaam (bijv. Italiaans)"
                        :state="newTagNameValid"
                        size="sm" />
                     <small class="text-muted">Naam die gebruikers zien</small>
                  </b-col>
                  <b-col cols="2">
                     <b-button type="submit" variant="primary" size="sm" :disabled="!canAddTag">
                        <b-icon icon="plus" /> Toevoegen
                     </b-button>
                  </b-col>
               </b-row>
            </b-form>

            <!-- Tags lijst -->
            <b-table 
               :items="tags" 
               :fields="tagFields" 
               small 
               striped 
               hover
               responsive>
               <template #cell(actions)="row">
                  <b-button 
                     v-if="editingTagId !== row.item.id"
                     size="sm" 
                     variant="outline-primary" 
                     @click="startEditTag(row.item)"
                     class="mr-1">
                     <b-icon icon="pencil" />
                  </b-button>
                  <b-button 
                     v-if="editingTagId === row.item.id"
                     size="sm" 
                     variant="success" 
                     @click="saveEditTag()"
                     class="mr-1">
                     <b-icon icon="check" />
                  </b-button>
                  <b-button 
                     v-if="editingTagId === row.item.id"
                     size="sm" 
                     variant="secondary" 
                     @click="cancelEditTag()"
                     class="mr-1">
                     <b-icon icon="x" />
                  </b-button>
                  <b-button 
                     v-if="editingTagId !== row.item.id"
                     size="sm" 
                     variant="outline-danger" 
                     @click="deleteTag(row.item)">
                     <b-icon icon="trash" />
                  </b-button>
               </template>
               <template #cell(name)="row">
                  <span v-if="editingTagId !== row.item.id">{{ row.item.name }}</span>
                  <b-form-input 
                     v-else 
                     v-model="editingTag.name" 
                     size="sm" />
               </template>
               <template #cell(id)="row">
                  <code>{{ row.item.id }}</code>
               </template>
            </b-table>
            <small class="text-muted">{{ tags.length }} tags geladen</small>
         </b-card-body>
      </b-card>
   </b-container>
</template>

<script>
import firebase from "firebase"
import "firebase/functions"
import db from "../db.js";
export default {
   name:"admin-main",
   props: ["user"],
   data: function() {
      return {
         tags: [],
         tagFields: [
            { key: 'id', label: 'ID', sortable: true },
            { key: 'name', label: 'Naam', sortable: true },
            { key: 'actions', label: 'Acties' }
         ],
         newTag: { id: '', name: '' },
         editingTagId: null,
         editingTag: { id: '', name: '' },
         email: "",
         uid: null,
         displayName: null,
         uidValid: null,
         displayNameValid: null,
      }
   },
   computed: {
      newTagIdValid: function() {
         if(!this.newTag.id) return null
         // Check lowercase, no spaces, no special chars
         const valid = /^[a-z0-9-]+$/.test(this.newTag.id)
         // Check not already exists
         const exists = this.tags.some(t => t.id === this.newTag.id)
         return valid && !exists
      },
      newTagNameValid: function() {
         if(!this.newTag.name) return null
         return this.newTag.name.length >= 2
      },
      canAddTag: function() {
         return this.newTagIdValid === true && this.newTagNameValid === true
      }
   },
   methods: {
      loadTags: function() {
         db.collection('tags').orderBy('name').onSnapshot(snapshot => {
            this.tags = []
            snapshot.forEach(doc => {
               const tag = doc.data()
               tag.id = doc.id
               this.tags.push(tag)
            })
         })
      },
      addTag: function() {
         if(!this.canAddTag) return
         const tag = { id: this.newTag.id, name: this.newTag.name }
         db.collection('tags').doc(tag.id).set(tag).then(() => {
            this.showToast(`Tag '${tag.name}' toegevoegd`, "Succes", "success")
            this.newTag = { id: '', name: '' }
         }).catch(error => {
            this.showToast(error.message, "Fout", "danger")
         })
      },
      startEditTag: function(tag) {
         this.editingTagId = tag.id
         this.editingTag = { id: tag.id, name: tag.name }
      },
      saveEditTag: function() {
         db.collection('tags').doc(this.editingTagId).update({ 
            name: this.editingTag.name 
         }).then(() => {
            this.showToast(`Tag '${this.editingTag.name}' bijgewerkt`, "Succes", "success")
            this.cancelEditTag()
         }).catch(error => {
            this.showToast(error.message, "Fout", "danger")
         })
      },
      cancelEditTag: function() {
         this.editingTagId = null
         this.editingTag = { id: '', name: '' }
      },
      deleteTag: function(tag) {
         if(!confirm(`Weet je zeker dat je tag '${tag.name}' wilt verwijderen?`)) return
         db.collection('tags').doc(tag.id).delete().then(() => {
            this.showToast(`Tag '${tag.name}' verwijderd`, "Succes", "warning")
         }).catch(error => {
            this.showToast(error.message, "Fout", "danger")
         })
      },
      switchUser: function(){
         this.uidValid = Boolean(this.uid && this.uid.length==28)
         this.displayNameValid = Boolean(this.displayName && this.displayName.length> 0)
         if(this.uidValid && this.displayNameValid) {
            this.$root.$emit("switchUser", {uid:this.uid, displayName:this.displayName})
         }
      },
      addAdminRole: function() {
         const addAdminRole = firebase.functions().httpsCallable('addAdminRole')
         addAdminRole({email: this.email}).then(result => {
            this.showToast(result.data.message, "Info", "success")
         }).catch(error => {
            this.showToast(error.message, "Info", "danger")
         })
      },
      showToast: function(message, title, variant = "default") { 
         this.$emit("showToast", 
            [message, 
            {  
               title: title,
               toaster: 'b-toaster-top-right',
               variant: variant,
               solid: true,
               appendToast: false
            }])
      },
      cleanRecipes:function(user) {
         var ref = db.collection("users").doc(user.uid);
         ref.collection("recipes").get().then((rcpCol) => {
            rcpCol.forEach( rcpDoc => { 
               let recipe = rcpDoc.data()
               console.log(recipe.naam)
               recipe = this.cleanIngredients(recipe)
               ref.collection('recipes').doc(rcpDoc.id).set(recipe).then(() => {
                  console.log(recipe.naam + " cleaned.")
               }).catch(error => {
                  console.log(error)
               })  
            })
         })
      },
      cleanIngredients:function(recipe) {
         for (let i = 0; i < recipe.ingredients.length; i++) {
            let ingredient = recipe.ingredients[i];
            if('selected' in ingredient){
               delete ingredient.selected
            }
         }
         return recipe
      }
   },
   mounted: function() {
      this.loadTags()
   }
}
</script>
